import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import NavBar from 'components/NavBar.jsx';
import { GECKO_CONSTS, GeckoCardTable, GeckoAvatar } from 'ui';
import { fromNow } from '../../utilities/fromNowWrapper.js';
import GeckoLocation from 'services/gecko-location';
import GeckoPermissions from 'services/gecko-permissions';
import GeckoPagination from 'services/gecko-pagination';
import { selectAppStateLoggedInUser } from '../../reducers/appState_selector';
import deleteAgentService from 'components/DeleteAgentModal/service';
import { sortObjects } from 'utilities/sorter';
import GeckoRequest from 'services/gecko-request/index.js';
import { renderError } from 'services/gecko-error';

const sortByLastSeen = (agent1, agent2) => {
  if (agent1.last_active === null && agent2.last_active === null) {
    return 0;
  }
  if (agent1.last_active === null) {
    return 1;
  }
  if (agent2.last_active === null) {
    return -1;
  }
  return Date.parse(agent2.last_active) - Date.parse(agent1.last_active);
};

const sortAscending = (agents, sortBy) => {
  switch (sortBy) {
    case 'last_active':
      return [...agents].sort(sortByLastSeen);
    default:
      return sortObjects(agents, sortBy, 'ASC');
  }
};

const sorter = (agents, sortBy, sortDirection) => {
  const sorted = sortAscending(agents, sortBy);
  if (sortDirection === 'ASC') {
    return sorted;
  }
  return sorted.reverse();
};

const navLinks = [
  {
    to: '/agents',
    activeClassName: 'active',
    linkText: 'Agents',
  },
  {
    to: '/teams',
    activeClassName: 'active',
    linkText: 'Teams',
  },
];

const columns = [
  {
    key: 'name',
    name: 'Name',
    render: (value) => {
      return (
        <div className="avatar-col">
          <GeckoAvatar
            name={value.full_name}
            first_name={value.first_name}
            last_name={value.last_name}
            color={value.avatar_colour}
            bgImage={value.profile_picture_url}
          />
          <span>&nbsp;{value.full_name}</span>
        </div>
      );
    },
    width: '25%',
    sortable: 'full_name',
  },
  {
    key: 'status',
    name: 'Status',
    align: 'left',
    type: GECKO_CONSTS.TABLE_CELL_TYPE_STATUS,
    styles: [
      {
        name: 'Online',
        type: GECKO_CONSTS.STATUS_TYPE_SUCCESS,
        key: 'Available',
      },
      {
        name: 'Unavailable',
        type: GECKO_CONSTS.STATUS_TYPE_WARNING,
        key: 'Unavailable',
      },
      {
        name: 'Offline',
        type: GECKO_CONSTS.STATUS_TYPE_DEFAULT,
        key: 'Offline',
      },
    ],
  },
  {
    key: 'email',
    name: 'Email',
    width: '25%',
    sortable: true,
  },
  {
    key: 'group.type',
    name: 'Type',
    width: '25%',
    render: (type) => {
      if (type) {
        const [firstLetter, ...rest] = type.split('');
        return `${firstLetter.toUpperCase()}${rest.join('')}`;
      }
      return '';
    },
  },
  {
    key: 'last_active',
    name: 'Last Seen',
    width: '25%',
    sortable: true,
    render: (value, row) => {
      if (row.presence === 'Online') {
        return 'Now';
      }
      return value ? fromNow(Date.parse(value)) : 'Never';
    },
  },
];

const rowHandler = (agent) => {
  return GeckoLocation.goTo(`/agents/${agent.id}`);
};

const getAgentCrossPlatformStatus = (agent) => {
  if (agent.presence === 'Online') {
    return agent.web_available || agent.mobile_available
      ? 'Available'
      : 'Unavailable';
  } else {
    return 'Offline';
  }
};

export const Agents = ({ params, agents }) => {
  const isAdmin = GeckoPermissions.memberOf('admin');
  const isAgent = GeckoPermissions.memberOf('agent');

  const searchedAgents = useMemo(() => {
    if (!params.search) {
      return agents;
    }
    return agents.filter((agent) => {
      return (
        agent.full_name.toUpperCase().indexOf(params.search.toUpperCase()) !==
        -1
      );
    });
  }, [params.search, agents]);

  const agentsWithCorrectPresence = useMemo(() => {
    return searchedAgents.map((agent) => {
      var profile_picture_url = agent.additional.profile_picture_url
        ? agent.additional.profile_picture_url
        : '';
      var avatar_colour = agent.additional.avatar_colour
        ? agent.additional.avatar_colour
        : '';
      agent.name = {
        full_name: agent.full_name,
        first_name: agent.first_name,
        last_name: agent.last_name,
        profile_picture_url: profile_picture_url,
        avatar_colour: avatar_colour,
      };
      agent.status = getAgentCrossPlatformStatus(agent);
      return agent;
    });
  }, [searchedAgents]);

  const [paginatedAgents, pagination] = useMemo(() => {
    return GeckoPagination.paginate({
      page: params.page,
      order_by: params.order_by,
      sort: params.sort,
      sorter,
    })(agentsWithCorrectPresence);
  }, [agentsWithCorrectPresence, params]);

  const setAgentsAvailability = (agent_id) => {
    GeckoRequest.post('/agent/set_as_unavailable', {
      agent_id,
    }).catch(renderError);
  };

  const isUnavailable = (agent) => {
    return !agent.web_available;
  };
  const isOffline = (agent) => {
    return agent.presence === 'Offline';
  };
  const dropdownMenu = {
    type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
    items: [
      {
        name: 'Set agent as unavailable',
        handler: (row) => setAgentsAvailability(row.id),
        hideWhen: (row) => isUnavailable(row) || isOffline(row),
      },
      {
        name: 'Delete',
        handler: (row) => deleteAgentService(row.id),
        hideWhen: () => !isAdmin,
      },
    ],
  };

  const tableColumns = useMemo(() => {
    if (isAgent) {
      return columns;
    }
    return [...columns, dropdownMenu];
  }, [isAgent]);

  const headerButtons = useMemo(() => {
    if (isAgent) {
      return [];
    }
    return [
      {
        preset: GECKO_CONSTS.BTN_PRESET_ADD,
        name: 'Invite Agent',
        handler: () => {
          return GeckoLocation.goTo('/agents/invite');
        },
      },
    ];
  }, [isAgent]);

  return (
    <div>
      <NavBar navLinks={navLinks} />
      <div className="content-margin">
        <GeckoCardTable
          name="Agents"
          cols={tableColumns}
          rows={paginatedAgents}
          headerBtns={headerButtons}
          rowHandler={rowHandler}
          pagination={pagination}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const agents = state.visible_agents.map((agent) => {
    const group = state.groups.find(
      (g) => Number(g.id) === Number(agent.group_id)
    );
    return Object.assign({}, agent, { group });
  });
  return {
    user: selectAppStateLoggedInUser(state),
    agents: agents,
  };
};

export default connect(mapStateToProps)(Agents);
